import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, BehaviorSubject, throwError } from 'rxjs';
import { map, tap, catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';

// ============================================================================
// INTERFACES
// ============================================================================

export interface ImageInfo {
  id: string;
  path: string;
  filename: string;
  name: string;
  url: string;
  thumbnailUrl?: string;
  size?: number;
  width?: number;
  height?: number;
  is_joined: boolean;
  thumbnail_base64?: string;
  savedToDb?: boolean;
  uploadedToGcs?: boolean;
}

export interface LevelState {
  index: number;
  label: string;
  images: ImageInfo[];
  selectedImages: string[];
  selected: string | null;
  lines: [number, number, number, number][];
  mode: 'vertical' | 'horizontal' | 'crop' | null;
  isHighlighted: boolean;
  undoStack: any[];
  redoStack: any[];
  cutRegistry: any[];
  joinHistory: { [key: string]: any };
  savedToDb?: boolean;
  uploadedToGcs?: boolean;
}

export interface SessionState {
  sessionId: string;
  levels: LevelState[];
  currentFolder: string | null;
  gcsEnabled: boolean;
  dbEnabled: boolean;
}

export interface ToolStatus {
  success: boolean;
  gcs_enabled: boolean;
  gcs_bucket: string | null;
  db_enabled: boolean;
  pdf_support: boolean;
}

export interface CutRequest {
  session_id: string;
  level_index: number;
  image_path: string;
  cut_type: 'vertical' | 'horizontal';
  line_positions: number[];
  line_coordinates: [number, number, number, number][];
}

export interface JoinRequest {
  session_id: string;
  level_index: number;
  image_paths: string[];
  join_type: 'vertical' | 'horizontal';
}

export interface CropRequest {
  session_id: string;
  level_index: number;
  image_path: string;
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface MarkLineRequest {
  session_id: string;
  level_index: number;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  is_vertical: boolean;
}

// ============================================================================
// SERVICE
// ============================================================================

@Injectable({
  providedIn: 'root'
})
export class ImageProcessingToolService {
  // Use dedicated image tool API URL if available, fallback to main API
  private apiBaseUrl = environment.imageToolApiUrl || environment.apiBaseUrl;
  
  // Session state - made public for component access
  private sessionId: string | null = null;
  private backendSessionId: string | null = null;  // Backend session ID (different from local)
  private _sessionState$ = new BehaviorSubject<SessionState | null>(null);
  
  // Public observable for session state
  public sessionState$ = this._sessionState$.asObservable();
  
  // Tool status
  private toolStatus$ = new BehaviorSubject<ToolStatus | null>(null);
  
  // Active level tracking
  private activeLevelIndex$ = new BehaviorSubject<number>(0);
  
  constructor(private http: HttpClient) {}

  // ============================================================================
  // SESSION MANAGEMENT
  // ============================================================================

  /**
   * Get the current session ID
   */
  getSessionId(): string | null {
    return this.sessionId;
  }

  /**
   * Get session state as observable
   */
  getSessionState(): Observable<SessionState | null> {
    return this.sessionState$;
  }

  /**
   * Get tool status as observable
   */
  getToolStatus(): Observable<ToolStatus | null> {
    return this.toolStatus$.asObservable();
  }

  /**
   * Get active level index as observable
   */
  getActiveLevelIndex(): Observable<number> {
    return this.activeLevelIndex$.asObservable();
  }

  /**
   * Set active level
   */
  setActiveLevel(index: number): void {
    this.activeLevelIndex$.next(index);
  }

  /**
   * Create a new session
   */
  createSession(): Observable<any> {
    return this.http.post<any>(`${this.apiBaseUrl}/api/image-tool/session`, {}).pipe(
      tap(response => {
        if (response.success) {
          this.sessionId = response.session_id;
          // Initialize session state with 5 levels by default (like Python)
          const state: SessionState = {
            sessionId: response.session_id,
            levels: [
              this.createEmptyLevel(0, 'Level-1'),
              this.createEmptyLevel(1, 'Level-2'),
              this.createEmptyLevel(2, 'Level-3'),
              this.createEmptyLevel(3, 'Level-4'),
              this.createEmptyLevel(4, 'Level-5')
            ],
            currentFolder: null,
            gcsEnabled: response.gcs_enabled,
            dbEnabled: response.db_enabled
          };
          this._sessionState$.next(state);
        }
      })
    );
  }

  /**
   * Delete session
   */
  deleteSession(): Observable<any> {
    if (!this.sessionId) {
      return new Observable(observer => {
        observer.next({ success: false, message: 'No session' });
        observer.complete();
      });
    }
    return this.http.delete<any>(`${this.apiBaseUrl}/api/image-tool/session/${this.sessionId}`).pipe(
      tap(() => {
        this.sessionId = null;
        this._sessionState$.next(null);
      })
    );
  }

  /**
   * Initialize session locally without API call
   * Creates a client-side session with 5 levels
   */
  initializeLocalSession(): void {
    // Generate a local session ID
    this.sessionId = 'local-' + Date.now().toString(36) + Math.random().toString(36).substr(2, 9);
    this.backendSessionId = null;  // Reset backend session
    
    // Initialize session state with 5 levels (like Python)
    const state: SessionState = {
      sessionId: this.sessionId,
      levels: [
        this.createEmptyLevel(0, 'Level-1'),
        this.createEmptyLevel(1, 'Level-2'),
        this.createEmptyLevel(2, 'Level-3'),
        this.createEmptyLevel(3, 'Level-4'),
        this.createEmptyLevel(4, 'Level-5')
      ],
      currentFolder: null,
      gcsEnabled: false,
      dbEnabled: false
    };
    this._sessionState$.next(state);
    console.log('[SESSION] Local session initialized:', this.sessionId);
  }

  /**
   * Ensure backend session exists for server-side operations (cut, crop, join)
   * Creates a new backend session if one doesn't exist
   * Returns the backend session ID
   */
  async ensureBackendSession(): Promise<string> {
    // If we already have a valid backend session, return it
    if (this.backendSessionId) {
      console.log('[SESSION] Using existing backend session:', this.backendSessionId);
      return this.backendSessionId;
    }

    // Create a new backend session
    console.log('[SESSION] Creating backend session for server operations...');
    
    try {
      const response = await this.http.post<any>(`${this.apiBaseUrl}/api/image-tool/session`, {}).toPromise();
      
      if (response.success) {
        this.backendSessionId = response.session_id;
        
        // Update session state with backend session info
        const state = this._sessionState$.value;
        if (state) {
          state.gcsEnabled = response.gcs_enabled;
          state.dbEnabled = response.db_enabled;
          this._sessionState$.next({ ...state });
        }
        
        console.log('[SESSION] Backend session created:', this.backendSessionId);
        console.log('[SESSION]   GCS enabled:', response.gcs_enabled);
        console.log('[SESSION]   DB enabled:', response.db_enabled);
        
        return this.backendSessionId!; // Non-null assertion since we just assigned it
      } else {
        throw new Error('Failed to create backend session');
      }
    } catch (error: any) {
      console.error('[SESSION] Failed to create backend session:', error);
      throw error;
    }
  }

  /**
   * Get the backend session ID (for API operations)
   */
  getBackendSessionId(): string | null {
    return this.backendSessionId;
  }

  private createEmptyLevel(index: number, label: string): LevelState {
    return {
      index,
      label,
      images: [],
      selectedImages: [],
      selected: null,
      lines: [],
      mode: null,
      isHighlighted: false,
      undoStack: [],
      redoStack: [],
      cutRegistry: [],
      joinHistory: {},
      savedToDb: false,
      uploadedToGcs: false
    };
  }

  /**
   * Add a new level dynamically (like Python - auto-add when cutting at last level)
   */
  addLevel(): LevelState | null {
    const state = this._sessionState$.value;
    if (!state) return null;
    
    const newIndex = state.levels.length;
    const newLevel = this.createEmptyLevel(newIndex, `Level-${newIndex + 1}`);
    state.levels.push(newLevel);
    this._sessionState$.next({ ...state });
    console.log(`[LEVEL] Added new level: Level-${newIndex + 1}`);
    return newLevel;
  }

  /**
   * Get total number of levels
   */
  getLevelCount(): number {
    return this._sessionState$.value?.levels.length || 0;
  }

  // ============================================================================
  // STATUS & INITIALIZATION
  // ============================================================================

  /**
   * Get tool status (GCS, DB, PDF support)
   */
  getStatus(): Observable<ToolStatus> {
    return this.http.get<ToolStatus>(`${this.apiBaseUrl}/api/image-tool/status`).pipe(
      tap(status => this.toolStatus$.next(status))
    );
  }

  // ============================================================================
  // FILE OPERATIONS
  // ============================================================================

  /**
   * Browse and load images from a folder
   */
  browseFolder(folderPath: string): Observable<any> {
    if (!this.sessionId) {
      throw new Error('No session created');
    }
    
    // Return an observable that ensures backend session exists first
    return new Observable(observer => {
      this.ensureBackendSession().then(backendSessionId => {
        const formData = new FormData();
        formData.append('session_id', backendSessionId);  // Use backend session ID
        formData.append('folder_path', folderPath);
        
        console.log('[BROWSE] Loading folder with backend session:', backendSessionId);
        
        this.http.post<any>(`${this.apiBaseUrl}/api/image-tool/browse-folder`, formData).pipe(
          tap(response => {
            if (response.success) {
              this.updateLevelImages(0, response.images, response.selected);
            }
          })
        ).subscribe({
          next: (result) => observer.next(result),
          error: (err) => observer.error(err),
          complete: () => observer.complete()
        });
      }).catch(err => {
        console.error('[BROWSE] Failed to ensure backend session:', err);
        observer.error(err);
      });
    });
  }

  /**
   * Upload files
   */
  uploadFiles(files: File[]): Observable<any> {
    if (!this.sessionId) {
      return new Observable(observer => {
        observer.error(new Error('No session created'));
      });
    }
    
    // Return an observable that ensures backend session exists first
    return new Observable(observer => {
      this.ensureBackendSession().then(backendSessionId => {
        const formData = new FormData();
        formData.append('session_id', backendSessionId);  // Use backend session ID
        files.forEach(file => formData.append('files', file));
        
        console.log('[UPLOAD] Uploading files to backend session:', backendSessionId);
        
        this.http.post<any>(`${this.apiBaseUrl}/api/image-tool/upload-files`, formData).pipe(
          tap(response => {
            if (response.success) {
              this.updateLevelImages(0, response.images, response.selected);
            }
          })
        ).subscribe({
          next: (result) => observer.next(result),
          error: (err) => observer.error(err),
          complete: () => observer.complete()
        });
      }).catch(err => {
        console.error('[UPLOAD] Failed to ensure backend session:', err);
        observer.error(err);
      });
    });
  }

  /**
   * Get images for a level
   */
  getLevelImages(levelIndex: number): Observable<any> {
    if (!this.sessionId) {
      throw new Error('No session created');
    }
    
    return this.http.get<any>(
      `${this.apiBaseUrl}/api/image-tool/levels/${this.sessionId}/${levelIndex}/images`
    ).pipe(
      tap(response => {
        if (response.success) {
          this.updateLevelImages(levelIndex, response.images, response.selected);
        }
      })
    );
  }

  private updateLevelImages(levelIndex: number, images: ImageInfo[], selected: string | null): void {
    const state = this._sessionState$.value;
    if (state && levelIndex < state.levels.length) {
      // Populate URL for each image using the API endpoint
      const imagesWithUrls = images.map(img => ({
        ...img,
        id: img.path, // Use path as ID if not provided
        filename: img.name || img.path.split('/').pop() || img.path.split('\\').pop() || 'image',
        url: this.getImageUrl(img.path),
        thumbnailUrl: img.thumbnail_base64 
          ? (img.thumbnail_base64.startsWith('data:') 
              ? img.thumbnail_base64 
              : `data:image/png;base64,${img.thumbnail_base64}`)
          : this.getImageUrl(img.path)
      }));
      
      state.levels[levelIndex].images = imagesWithUrls;
      state.levels[levelIndex].selected = selected;
      
      // Clear downstream levels
      for (let i = levelIndex + 1; i < state.levels.length; i++) {
        state.levels[i].images = [];
        state.levels[i].selected = null;
        state.levels[i].lines = [];
      }
      
      this._sessionState$.next({ ...state });
    }
  }

  // ============================================================================
  // IMAGE DISPLAY
  // ============================================================================

  /**
   * Get image as base64 with optional lines
   */
  getImageBase64(imagePath: string, withLines: boolean = false): Observable<any> {
    if (!this.sessionId) {
      throw new Error('No session created');
    }
    
    const params = new HttpParams()
      .set('session_id', this.sessionId)
      .set('image_path', imagePath)
      .set('with_lines', withLines.toString());
    
    return this.http.get<any>(`${this.apiBaseUrl}/api/image-tool/image-base64`, { params });
  }

  /**
   * Get image URL for direct display
   */
  getImageUrl(imagePath: string, withLines: boolean = false): string {
    // Use backend session ID if available (for server-side images from cut/crop/join)
    // Otherwise fall back to local session ID
    const sessionId = this.backendSessionId || this.sessionId;
    if (!sessionId) {
      return '';
    }
    
    const params = new URLSearchParams({
      session_id: sessionId,
      image_path: imagePath,
      with_lines: withLines.toString()
    });
    
    return `${this.apiBaseUrl}/api/image-tool/image?${params.toString()}`;
  }

  /**
   * Select an image at a level
   */
  selectImage(levelIndex: number, imagePath: string): Observable<any> {
    if (!this.sessionId) {
      throw new Error('No session created');
    }
    
    const formData = new FormData();
    formData.append('session_id', this.sessionId);
    formData.append('level_index', levelIndex.toString());
    formData.append('image_path', imagePath);
    
    return this.http.post<any>(`${this.apiBaseUrl}/api/image-tool/select-image`, formData).pipe(
      tap(response => {
        if (response.success) {
          const state = this._sessionState$.value;
          if (state) {
            state.levels[levelIndex].selected = imagePath;
            // Clear downstream levels
            for (let i = levelIndex + 1; i < state.levels.length; i++) {
              state.levels[i].images = [];
              state.levels[i].selected = null;
              state.levels[i].lines = [];
            }
            this._sessionState$.next({ ...state });
          }
        }
      })
    );
  }

  // ============================================================================
  // LINE MARKING
  // ============================================================================

  /**
   * Mark a line on an image
   */
  markLine(request: MarkLineRequest): Observable<any> {
    console.log('[MARK] Marking line:', { level: request.level_index, isVertical: request.is_vertical, coords: [request.x1, request.y1, request.x2, request.y2] });
    
    return this.http.post<any>(`${this.apiBaseUrl}/api/image-tool/mark-line`, request).pipe(
      tap(response => {
        if (response.success) {
          console.log('[MARK] Line marked successfully, total lines:', response.lines?.length);
          const state = this._sessionState$.value;
          if (state && request.level_index < state.levels.length) {
            state.levels[request.level_index].lines = response.lines;
            this._sessionState$.next({ ...state });
          }
        } else {
          console.error('[MARK] Failed to mark line:', response.message);
        }
      })
    );
  }

  /**
   * Clear all lines at a level
   */
  clearLines(levelIndex: number): Observable<any> {
    if (!this.sessionId) {
      throw new Error('No session created');
    }
    
    return this.http.post<any>(`${this.apiBaseUrl}/api/image-tool/clear-lines`, {
      session_id: this.sessionId,
      level_index: levelIndex
    }).pipe(
      tap(response => {
        if (response.success) {
          const state = this._sessionState$.value;
          if (state && levelIndex < state.levels.length) {
            state.levels[levelIndex].lines = [];
            this._sessionState$.next({ ...state });
          }
        }
      })
    );
  }

  /**
   * Add line locally (for immediate UI update)
   */
  addLineLocally(levelIndex: number, line: [number, number, number, number]): void {
    const state = this._sessionState$.value;
    if (state && levelIndex < state.levels.length) {
      state.levels[levelIndex].lines.push(line);
      this._sessionState$.next({ ...state });
    }
  }

  /**
   * Set cutting mode for a level
   */
  setCuttingMode(levelIndex: number, mode: 'vertical' | 'horizontal' | 'crop' | null): void {
    const state = this._sessionState$.value;
    if (state && levelIndex < state.levels.length) {
      state.levels[levelIndex].mode = mode;
      this._sessionState$.next({ ...state });
    }
  }

  // ============================================================================
  // CUT OPERATIONS
  // ============================================================================

  /**
   * Cut an image
   */
  cutImage(request: CutRequest): Observable<any> {
    console.log('[CUT] Sending cut request:', { level: request.level_index, cutType: request.cut_type, positions: request.line_positions });
    
    return this.http.post<any>(`${this.apiBaseUrl}/api/image-tool/cut`, request).pipe(
      tap(response => {
        console.log('[CUT] Received response:', response);
        
        if (response.success) {
          const state = this._sessionState$.value;
          if (state) {
            // Load results into next level
            const nextLevel = request.level_index + 1;
            
            // Auto-add a new level if cutting at the last level (like Python)
            if (nextLevel >= state.levels.length) {
              console.log(`[CUT] Cutting at last level (${request.level_index + 1}), adding Level-${nextLevel + 1}`);
              this.addLevel();
            }
            
            if (nextLevel < state.levels.length && response.output_files?.length > 0) {
              // Populate URLs for each output image
              const imagesWithUrls = response.output_files.map((img: any) => ({
                ...img,
                id: img.path || img.id,
                filename: img.name || img.path?.split('/').pop() || img.path?.split('\\').pop() || 'cut_image',
                url: this.getImageUrl(img.path),
                thumbnailUrl: img.thumbnail_base64 
                  ? (img.thumbnail_base64.startsWith('data:') 
                      ? img.thumbnail_base64 
                      : `data:image/png;base64,${img.thumbnail_base64}`)
                  : this.getImageUrl(img.path)
              }));
              
              console.log('[CUT] Updating next level with images:', imagesWithUrls.length);
              state.levels[nextLevel].images = imagesWithUrls;
              state.levels[nextLevel].selected = imagesWithUrls[0]?.id || null;
              state.levels[nextLevel].selectedImages = [];
            }
            
            // Update undo stack
            state.levels[request.level_index].cutRegistry.push({
              cut_id: response.cut_id,
              output_files: response.output_files
            });
            
            // Clear lines after successful cut
            state.levels[request.level_index].lines = [];
            
            this._sessionState$.next({ ...state });
          }
        } else {
          console.error('[CUT] Cut failed:', response.message || 'Unknown error');
        }
      })
    );
  }

  /**
   * Crop an image - replaces the original file like Python UI
   */
  cropImage(request: CropRequest): Observable<any> {
    console.log('[CROP] Sending crop request:', { level: request.level_index, x: request.x, y: request.y, w: request.width, h: request.height });
    
    return this.http.post<any>(`${this.apiBaseUrl}/api/image-tool/crop`, request).pipe(
      tap(response => {
        console.log('[CROP] Received response:', response);
        
        if (response.success) {
          // Crop replaces the same file, so we need to refresh the level
          // to update the thumbnail and image display
          console.log('[CROP] Crop successful, refreshing level images...');
          this.getLevelImages(request.level_index).subscribe({
            next: () => console.log('[CROP] Level images refreshed'),
            error: (err) => console.error('[CROP] Failed to refresh level images:', err)
          });
        } else {
          console.error('[CROP] Crop failed:', response.message || 'Unknown error');
        }
      })
    );
  }

  // ============================================================================
  // JOIN OPERATIONS
  // ============================================================================

  /**
   * Join images
   */
  joinImages(request: JoinRequest): Observable<any> {
    return this.http.post<any>(`${this.apiBaseUrl}/api/image-tool/join`, request).pipe(
      tap(response => {
        if (response.success) {
          // Refresh level images
          this.getLevelImages(request.level_index).subscribe();
        }
      })
    );
  }

  // ============================================================================
  // UNDO/REDO
  // ============================================================================

  /**
   * Undo last operation
   */
  undo(levelIndex: number): Observable<any> {
    if (!this.sessionId) {
      throw new Error('No session created');
    }
    
    return this.http.post<any>(`${this.apiBaseUrl}/api/image-tool/undo`, {
      session_id: this.sessionId,
      level_index: levelIndex
    });
  }

  /**
   * Redo last undone operation
   */
  redo(levelIndex: number): Observable<any> {
    if (!this.sessionId) {
      throw new Error('No session created');
    }
    
    return this.http.post<any>(`${this.apiBaseUrl}/api/image-tool/redo`, {
      session_id: this.sessionId,
      level_index: levelIndex
    });
  }

  // ============================================================================
  // SAVE TO DATABASE
  // ============================================================================

  /**
   * Save cuts to database
   */
  saveCuts(levelIndex: number, uploadToGcs: boolean = true): Observable<any> {
    // Use backend session ID for save since we're saving server-side files
    const sessionId = this.backendSessionId || this.sessionId;
    if (!sessionId) {
      throw new Error('No session created');
    }
    
    console.log('[SAVE] Using session ID:', sessionId, '(backend:', !!this.backendSessionId, ')');
    
    return this.http.post<any>(`${this.apiBaseUrl}/api/image-tool/save`, {
      session_id: sessionId,
      level_index: levelIndex,
      upload_to_gcs: uploadToGcs
    });
  }

  // ============================================================================
  // LEVEL STATE MANAGEMENT
  // ============================================================================

  /**
   * Set level highlight state
   */
  setLevelHighlight(levelIndex: number, highlighted: boolean): void {
    const state = this._sessionState$.value;
    if (state && levelIndex < state.levels.length) {
      // Remove highlight from all levels
      state.levels.forEach((level, i) => {
        level.isHighlighted = (i === levelIndex && highlighted);
      });
      this._sessionState$.next({ ...state });
    }
  }

  /**
   * Navigate to next level
   */
  navigateToNextLevel(): void {
    const currentIndex = this.activeLevelIndex$.value;
    const state = this._sessionState$.value;
    if (state) {
      const nextIndex = (currentIndex + 1) % state.levels.length;
      this.setActiveLevel(nextIndex);
      this.setLevelHighlight(nextIndex, true);
    }
  }

  /**
   * Navigate to previous level
   */
  navigateToPreviousLevel(): void {
    const currentIndex = this.activeLevelIndex$.value;
    const state = this._sessionState$.value;
    if (state) {
      const prevIndex = (currentIndex - 1 + state.levels.length) % state.levels.length;
      this.setActiveLevel(prevIndex);
      this.setLevelHighlight(prevIndex, true);
    }
  }

  /**
   * Reset all levels
   */
  resetAll(): void {
    const state = this._sessionState$.value;
    if (state) {
      state.levels.forEach(level => {
        level.images = [];
        level.selectedImages = [];
        level.selected = null;
        level.lines = [];
        level.mode = null;
        level.undoStack = [];
        level.redoStack = [];
        level.cutRegistry = [];
        level.joinHistory = {};
        level.savedToDb = false;
        level.uploadedToGcs = false;
      });
      state.currentFolder = null;
      this._sessionState$.next({ ...state });
    }
  }

  // ============================================================================
  // ADDITIONAL METHODS FOR IMAGE PROCESSING TOOL COMPONENT
  // ============================================================================

  /**
   * Close the current session (alias for deleteSession)
   */
  closeSession(): Observable<any> {
    return this.deleteSession();
  }

  /**
   * Convert PDF to images
   */
  convertPdfToImages(file: File, levelIndex: number): Observable<any> {
    if (!this.sessionId) {
      throw new Error('No session created');
    }
    
    const formData = new FormData();
    formData.append('session_id', this.sessionId);
    formData.append('file', file);
    formData.append('level_index', levelIndex.toString());
    
    return this.http.post<any>(`${this.apiBaseUrl}/api/image-tool/convert-pdf`, formData).pipe(
      tap(response => {
        if (response.success) {
          this.updateLevelImages(levelIndex, response.images, response.selected);
        }
      })
    );
  }

  /**
   * Load images from file list
   */
  loadImages(files: FileList, levelIndex: number): Observable<any> {
    if (!this.sessionId) {
      throw new Error('No session created');
    }
    
    const formData = new FormData();
    formData.append('session_id', this.sessionId);
    formData.append('level_index', levelIndex.toString());
    
    Array.from(files).forEach(file => {
      formData.append('files', file);
    });
    
    return this.http.post<any>(`${this.apiBaseUrl}/api/image-tool/upload-files`, formData).pipe(
      tap(response => {
        if (response.success) {
          this.updateLevelImages(levelIndex, response.images, response.selected);
        }
      })
    );
  }

  /**
   * Check if an image is loaded locally (not yet uploaded to server)
   */
  private isLocalImage(image: ImageInfo): boolean {
    // Image is local if its id starts with "local_" or url is a base64 data URL
    return (image.id?.startsWith('local_') || image.url?.startsWith('data:')) ?? false;
  }

  /**
   * Upload a local base64 image to the server and return the server path
   */
  private async uploadLocalImage(image: ImageInfo, levelIndex: number, backendSessionId: string): Promise<string> {
    console.log('[UPLOAD] Uploading local image to server:', image.filename);
    
    // Convert base64 to Blob
    const base64Data = image.url || image.thumbnail_base64;
    if (!base64Data || !base64Data.startsWith('data:')) {
      throw new Error('No base64 data available for local image');
    }
    
    // Extract base64 content and convert to blob
    const parts = base64Data.split(',');
    const mime = parts[0].match(/:(.*?);/)?.[1] || 'image/png';
    const bstr = atob(parts[1]);
    const n = bstr.length;
    const u8arr = new Uint8Array(n);
    for (let i = 0; i < n; i++) {
      u8arr[i] = bstr.charCodeAt(i);
    }
    const blob = new Blob([u8arr], { type: mime });
    const file = new File([blob], image.filename || image.name || 'image.png', { type: mime });
    
    // Upload to server
    const formData = new FormData();
    formData.append('session_id', backendSessionId);
    formData.append('files', file);
    
    const response = await this.http.post<any>(`${this.apiBaseUrl}/api/image-tool/upload-files`, formData).toPromise();
    
    if (!response?.success || !response.images?.length) {
      throw new Error('Failed to upload image to server');
    }
    
    const uploadedImage = response.images[0];
    const serverPath = uploadedImage.path;
    console.log('[UPLOAD] Local image uploaded, server path:', serverPath);
    
    // Update the image in session state with server path
    const state = this._sessionState$.value;
    if (state && levelIndex < state.levels.length) {
      const imgIndex = state.levels[levelIndex].images.findIndex(img => img.id === image.id);
      if (imgIndex !== -1) {
        state.levels[levelIndex].images[imgIndex] = {
          ...state.levels[levelIndex].images[imgIndex],
          path: serverPath,
          id: serverPath, // Update ID to match server path
        };
        this._sessionState$.next({ ...state });
      }
    }
    
    return serverPath;
  }

  /**
   * Cut image at specific level with cut line (single line - kept for backward compatibility)
   */
  cutImageAtLine(levelIndex: number, imageId: string, cutLine: number, cutType: 'horizontal' | 'vertical' = 'horizontal'): Observable<any> {
    return this.cutImageAtLines(levelIndex, imageId, [cutLine], cutType);
  }

  /**
   * Cut image at specific level with multiple cut lines
   */
  cutImageAtLines(levelIndex: number, imageId: string, cutLines: number[], cutType: 'horizontal' | 'vertical' = 'horizontal'): Observable<any> {
    if (!this.sessionId) {
      console.error('[CUT] No session created');
      return throwError(() => new Error('No session created'));
    }
    
    const state = this._sessionState$.value;
    const image = state?.levels[levelIndex]?.images.find(img => img.id === imageId);
    
    if (!image) {
      console.error('[CUT] Image not found:', imageId);
      return throwError(() => new Error('Image not found'));
    }
    
    const imageWidth = image.width || 1000;
    const imageHeight = image.height || 1000;
    
    // Create line coordinates for all cut lines based on cut type
    let lineCoordinates: [number, number, number, number][];
    if (cutType === 'horizontal') {
      lineCoordinates = cutLines.map(cutLine => [0, cutLine, imageWidth, cutLine] as [number, number, number, number]);
    } else {
      lineCoordinates = cutLines.map(cutLine => [cutLine, 0, cutLine, imageHeight] as [number, number, number, number]);
    }
    
    // Return observable that ensures backend session exists first
    return new Observable(observer => {
      this.ensureBackendSession().then(async backendSessionId => {
        try {
          // Check if image is local and needs to be uploaded first
          let imagePath = image.path;
          if (this.isLocalImage(image)) {
            console.log('[CUT] Image is local, uploading to server first...');
            imagePath = await this.uploadLocalImage(image, levelIndex, backendSessionId);
          }
          
          const request: CutRequest = {
            session_id: backendSessionId,  // Use backend session ID
            level_index: levelIndex,
            image_path: imagePath,  // Use server path (either original or newly uploaded)
            cut_type: cutType,
            line_positions: cutLines,
            line_coordinates: lineCoordinates
          };
          
          console.log('[CUT] Cutting image at lines:', { imageId, cutLines, cutType, imagePath, backendSessionId });
          this.cutImage(request).subscribe({
            next: (result) => observer.next(result),
            error: (err) => observer.error(err),
            complete: () => observer.complete()
          });
        } catch (uploadErr) {
          console.error('[CUT] Failed to upload local image:', uploadErr);
          observer.error(uploadErr);
        }
      }).catch(err => {
        console.error('[CUT] Failed to ensure backend session:', err);
        observer.error(err);
      });
    });
  }

  /**
   * Crop image with rectangle
   */
  cropImageWithRect(levelIndex: number, imageId: string, cropRect: { x: number; y: number; width: number; height: number }): Observable<any> {
    if (!this.sessionId) {
      return throwError(() => new Error('No session created'));
    }
    
    const state = this._sessionState$.value;
    const image = state?.levels[levelIndex]?.images.find(img => img.id === imageId);
    
    if (!image) {
      return throwError(() => new Error('Image not found'));
    }
    
    // Return observable that ensures backend session exists first
    return new Observable(observer => {
      this.ensureBackendSession().then(async backendSessionId => {
        try {
          // Check if image is local and needs to be uploaded first
          let imagePath = image.path;
          if (this.isLocalImage(image)) {
            console.log('[CROP] Image is local, uploading to server first...');
            imagePath = await this.uploadLocalImage(image, levelIndex, backendSessionId);
          }
          
          // Convert to integers - Python API expects int types, not float
          const request: CropRequest = {
            session_id: backendSessionId,  // Use backend session ID
            level_index: levelIndex,
            image_path: imagePath,  // Use server path
            x: Math.round(cropRect.x),
            y: Math.round(cropRect.y),
            width: Math.round(cropRect.width),
            height: Math.round(cropRect.height)
          };
          
          console.log('[CROP] Sending crop request:', request);
          this.cropImage(request).subscribe({
            next: (result) => observer.next(result),
            error: (err) => observer.error(err),
            complete: () => observer.complete()
          });
        } catch (uploadErr) {
          console.error('[CROP] Failed to upload local image:', uploadErr);
          observer.error(uploadErr);
        }
      }).catch(err => {
        console.error('[CROP] Failed to ensure backend session:', err);
        observer.error(err);
      });
    });
  }

  /**
   * Join selected images
   */
  joinSelectedImages(levelIndex: number, imageIds: string[], direction: 'vertical' | 'horizontal'): Observable<any> {
    if (!this.sessionId) {
      return throwError(() => new Error('No session created'));
    }
    
    const state = this._sessionState$.value;
    const images = state?.levels[levelIndex]?.images.filter(img => imageIds.includes(img.id)) || [];
    
    // Return observable that ensures backend session exists first
    return new Observable(observer => {
      this.ensureBackendSession().then(async backendSessionId => {
        try {
          // Upload any local images first
          const imagePaths: string[] = [];
          for (const image of images) {
            if (this.isLocalImage(image)) {
              console.log('[JOIN] Image is local, uploading to server first:', image.filename);
              const serverPath = await this.uploadLocalImage(image, levelIndex, backendSessionId);
              imagePaths.push(serverPath);
            } else {
              imagePaths.push(image.path);
            }
          }
          
          const request: JoinRequest = {
            session_id: backendSessionId,  // Use backend session ID
            level_index: levelIndex,
            image_paths: imagePaths,
            join_type: direction
          };
          
          console.log('[JOIN] Sending join request:', request);
          this.joinImages(request).subscribe({
            next: (result) => observer.next(result),
            error: (err) => observer.error(err),
            complete: () => observer.complete()
          });
        } catch (uploadErr) {
          console.error('[JOIN] Failed to upload local images:', uploadErr);
          observer.error(uploadErr);
        }
      }).catch(err => {
        console.error('[JOIN] Failed to ensure backend session:', err);
        observer.error(err);
      });
    });
  }

  /**
   * Undo join operation
   */
  undoJoin(levelIndex: number): Observable<any> {
    return this.undo(levelIndex);
  }

  /**
   * Move images to a different level
   */
  moveToLevel(fromLevel: number, toLevel: number, imageIds: string[]): Observable<any> {
    if (!this.sessionId) {
      throw new Error('No session created');
    }
    
    return this.http.post<any>(`${this.apiBaseUrl}/api/image-tool/move-to-level`, {
      session_id: this.sessionId,
      from_level: fromLevel,
      to_level: toLevel,
      image_ids: imageIds
    }).pipe(
      tap(response => {
        if (response.success) {
          // Refresh both levels
          this.getLevelImages(fromLevel).subscribe();
          this.getLevelImages(toLevel).subscribe();
        }
      })
    );
  }

  /**
   * Delete images
   */
  deleteImages(levelIndex: number, imageIds: string[]): Observable<any> {
    if (!this.sessionId) {
      console.error('[DELETE] No session created');
      throw new Error('No session created');
    }
    
    console.log('[DELETE] Deleting images:', { levelIndex, count: imageIds.length, imageIds });
    
    // Use FormData as the API expects Form parameters
    const formData = new FormData();
    formData.append('session_id', this.sessionId);
    formData.append('level_index', levelIndex.toString());
    formData.append('image_ids', JSON.stringify(imageIds));
    formData.append('hierarchical', 'false');  // Non-hierarchical by default
    
    return this.http.post<any>(`${this.apiBaseUrl}/api/image-tool/delete-images`, formData).pipe(
      tap(response => {
        if (response.success) {
          console.log('[DELETE] Successfully deleted images');
          this.getLevelImages(levelIndex).subscribe();
        } else {
          console.error('[DELETE] Failed to delete images:', response.message);
        }
      })
    );
  }

  /**
   * Hierarchical delete: Delete all cuts at specified level and all subsequent levels
   * Matches Python's _hierarchical_delete_all() functionality with full database integration
   */
  hierarchicalDeleteLevel(levelIndex: number): Observable<any> {
    if (!this.sessionId) {
      console.error('[HIERARCHICAL_DELETE] No session created');
      throw new Error('No session created');
    }
    
    console.log(`[HIERARCHICAL_DELETE] Deleting level ${levelIndex + 1} and all subsequent levels`);
    
    const formData = new FormData();
    formData.append('session_id', this.sessionId);
    formData.append('level_index', levelIndex.toString());
    
    return this.http.post<any>(`${this.apiBaseUrl}/api/image-tool/hierarchical-delete`, formData).pipe(
      tap(response => {
        if (response.success) {
          console.log('[HIERARCHICAL_DELETE] Success:', response.message);
          console.log(`[HIERARCHICAL_DELETE] - Levels cleared: ${response.levels_cleared}`);
          console.log(`[HIERARCHICAL_DELETE] - DB cuts deleted: ${response.db_cuts_deleted}`);
          console.log(`[HIERARCHICAL_DELETE] - Files deleted: ${response.files_deleted}`);
          
          // Refresh all affected levels
          for (let i = levelIndex; i < 5; i++) {
            this.getLevelImages(i).subscribe();
          }
        } else {
          console.error('[HIERARCHICAL_DELETE] Failed:', response.message);
        }
      }),
      catchError(error => {
        console.error('[HIERARCHICAL_DELETE] Error:', error);
        throw error;
      })
    );
  }

  /**
   * Clear a level and all subsequent levels (hierarchical clear)
   * This calls the hierarchical delete API
   */
  clearLevelHierarchical(levelIndex: number): Observable<any> {
    console.log(`[CLEAR_HIERARCHICAL] Clearing level ${levelIndex + 1} and beyond`);
    return this.hierarchicalDeleteLevel(levelIndex);
  }

  /**
   * Save to database
   */
  saveToDatabase(levelIndex: number): Observable<any> {
    console.log('[SAVE] Saving level to database:', levelIndex);
    return this.saveCuts(levelIndex, false);
  }

  /**
   * Upload to GCS
   */
  uploadToGcs(levelIndex: number): Observable<any> {
    return this.saveCuts(levelIndex, true);
  }

  /**
   * Toggle image selection
   */
  toggleImageSelection(levelIndex: number, imageId: string, selected: boolean): void {
    const state = this._sessionState$.value;
    if (state && levelIndex < state.levels.length) {
      const level = state.levels[levelIndex];
      if (selected) {
        if (!level.selectedImages.includes(imageId)) {
          level.selectedImages.push(imageId);
        }
      } else {
        level.selectedImages = level.selectedImages.filter(id => id !== imageId);
      }
      this._sessionState$.next({ ...state });
    }
  }

  /**
   * Clear all selections for a level
   */
  clearSelections(levelIndex: number): void {
    const state = this._sessionState$.value;
    if (state && levelIndex < state.levels.length) {
      state.levels[levelIndex].selectedImages = [];
      this._sessionState$.next({ ...state });
    }
  }

  /**
   * Select all images in a level
   */
  selectAllImages(levelIndex: number): void {
    const state = this._sessionState$.value;
    if (state && levelIndex < state.levels.length) {
      state.levels[levelIndex].selectedImages = state.levels[levelIndex].images.map(img => img.id);
      this._sessionState$.next({ ...state });
    }
  }

  /**
   * Open image in Windows default image viewer
   */
  openImageExternal(levelIndex: number, imagePath: string): Observable<any> {
    // Use backend session ID if available (for server-side images)
    const sessionId = this.backendSessionId || this._sessionState$.value?.sessionId;
    if (!sessionId) {
      return throwError(() => new Error('No active session'));
    }
    
    // Use FormData as the API expects Form parameters
    const formData = new FormData();
    formData.append('session_id', sessionId);
    formData.append('level_index', levelIndex.toString());
    formData.append('image_path', imagePath);
    
    return this.http.post<any>(`${this.apiBaseUrl}/api/image-tool/open-external`, formData);
  }

  // ============================================================================
  // LOCAL FILE LOADING (Like Python UI - No API calls for browsing)
  // ============================================================================

  /**
   * Load images locally from File objects without calling API
   * This mimics Python UI behavior where browsing shows images immediately
   */
  loadImagesLocally(files: File[], levelIndex: number = 0): Observable<ImageInfo[]> {
    console.log(`[LOCAL] Loading ${files.length} files locally to level ${levelIndex}`);
    
    return new Observable(observer => {
      const imageInfos: ImageInfo[] = [];
      let processed = 0;
      
      files.forEach((file, index) => {
        const reader = new FileReader();
        
        reader.onload = (e) => {
          const base64Data = e.target?.result as string;
          
          // Create image info from local file
          const imageInfo: ImageInfo = {
            id: `local_${Date.now()}_${index}_${file.name}`,
            path: file.name, // Use filename as path for local files
            filename: file.name,
            name: file.name,
            url: base64Data, // Use base64 data URL directly
            thumbnailUrl: base64Data,
            size: file.size,
            is_joined: false,
            thumbnail_base64: base64Data
          };
          
          // Get image dimensions
          const img = new Image();
          img.onload = () => {
            imageInfo.width = img.width;
            imageInfo.height = img.height;
            imageInfos.push(imageInfo);
            processed++;
            
            console.log(`[LOCAL] Loaded file ${processed}/${files.length}: ${file.name} (${img.width}x${img.height})`);
            
            if (processed === files.length) {
              // Sort by name to maintain order
              imageInfos.sort((a, b) => a.name.localeCompare(b.name));
              
              // Update session state with local images
              const state = this._sessionState$.value;
              if (state && levelIndex < state.levels.length) {
                state.levels[levelIndex].images = imageInfos;
                state.levels[levelIndex].selectedImages = [];
                state.levels[levelIndex].selected = imageInfos[0]?.id || null;
                this._sessionState$.next({ ...state });
                
                // Auto-select first image (like Python UI)
                console.log(`[LOCAL] Auto-selecting first image: ${imageInfos[0]?.name}`);
              }
              
              console.log(`[LOCAL] All ${files.length} files loaded locally`);
              observer.next(imageInfos);
              observer.complete();
            }
          };
          img.onerror = () => {
            console.error(`[LOCAL] Failed to get dimensions for ${file.name}`);
            imageInfos.push(imageInfo);
            processed++;
            
            if (processed === files.length) {
              imageInfos.sort((a, b) => a.name.localeCompare(b.name));
              
              const state = this._sessionState$.value;
              if (state && levelIndex < state.levels.length) {
                state.levels[levelIndex].images = imageInfos;
                state.levels[levelIndex].selectedImages = [];
                state.levels[levelIndex].selected = imageInfos[0]?.id || null;
                this._sessionState$.next({ ...state });
                
                // Auto-select first image (like Python UI)
                console.log(`[LOCAL] Auto-selecting first image (error case): ${imageInfos[0]?.name}`);
              }
              
              observer.next(imageInfos);
              observer.complete();
            }
          };
          img.src = base64Data;
        };
        
        reader.onerror = () => {
          console.error(`[LOCAL] Failed to read file: ${file.name}`);
          processed++;
          if (processed === files.length) {
            observer.next(imageInfos);
            observer.complete();
          }
        };
        
        reader.readAsDataURL(file);
      });
      
      // Handle empty file list
      if (files.length === 0) {
        observer.next([]);
        observer.complete();
      }
    });
  }

  /**
   * Load hierarchy from database for a source image
   * This is called after loading an image to check if it has saved cuts
   */
  loadHierarchyFromDatabase(sourceImagePath: string): Observable<any> {
    console.log(`[HIERARCHY] Loading hierarchy from database for: ${sourceImagePath}`);
    
    return this.http.get<any>(`${this.apiBaseUrl}/api/image-tool/hierarchy`, {
      params: {
        image_path: sourceImagePath,
        session_id: this.sessionId || ''
      }
    }).pipe(
      tap(response => {
        if (response.success && response.hierarchy) {
          console.log(`[HIERARCHY] Found hierarchy with ${response.hierarchy.length || 0} levels`);
          this.applyHierarchyToLevels(response.hierarchy);
        } else {
          console.log(`[HIERARCHY] No hierarchy found for this image`);
        }
      })
    );
  }

  /**
   * Apply loaded hierarchy to all levels
   */
  private applyHierarchyToLevels(hierarchy: any): void {
    console.log('[HIERARCHY_APPLY] ========== Starting hierarchy application ==========');
    
    const state = this._sessionState$.value;
    if (!state) {
      console.log('[HIERARCHY_APPLY] ERROR: Cannot apply - no session state');
      return;
    }
    
    console.log(`[HIERARCHY_APPLY] Session has ${state.levels?.length || 0} levels`);
    console.log('[HIERARCHY_APPLY] Hierarchy data:', JSON.stringify(hierarchy, null, 2));
    
    // hierarchy is expected to be an array of level data
    // Each level entry has: { levelIndex, images: [...], lines: [...] }
    // levelIndex is 0-based (0 = Level 1, 1 = Level 2, etc.)
    
    if (Array.isArray(hierarchy)) {
      console.log(`[HIERARCHY_APPLY] Processing ${hierarchy.length} level(s) from hierarchy`);
      
      hierarchy.forEach((levelData: any) => {
        const levelIndex = levelData.levelIndex ?? levelData.level_index ?? 0;
        
        console.log(`[HIERARCHY_APPLY] Processing levelIndex=${levelIndex} (UI Level ${levelIndex + 1}), images=${levelData.images?.length || 0}`);
        
        if (levelIndex >= 0 && levelIndex < state.levels.length) {
          console.log(`[HIERARCHY_APPLY] Level ${levelIndex + 1}: Valid index, processing images...`);
          
          // Load images for this level - only if they exist on disk
          if (levelData.images && Array.isArray(levelData.images)) {
            // Filter to only include existing files if the exists flag is present
            const existingImages = levelData.images.filter((img: any) => 
              img.exists === undefined || img.exists === true
            );
            
            const missingCount = levelData.images.length - existingImages.length;
            if (missingCount > 0) {
              console.log(`[HIERARCHY_APPLY] Level ${levelIndex + 1}: ${missingCount} files missing from disk, skipping them`);
            }
            
            if (existingImages.length > 0) {
              console.log(`[HIERARCHY_APPLY] Level ${levelIndex + 1}: Creating ${existingImages.length} image objects with URLs...`);
              
              const imagesWithUrls = existingImages.map((img: any) => {
                const imageObj = {
                  ...img,
                  id: img.path || img.id,
                  filename: img.filename || img.name || img.path?.split('/').pop() || img.path?.split('\\').pop() || 'image',
                  name: img.filename || img.name || img.path?.split('/').pop() || img.path?.split('\\').pop() || 'image',
                  url: img.url || (img.thumbnail_base64 
                    ? (img.thumbnail_base64.startsWith('data:') 
                        ? img.thumbnail_base64 
                        : `data:image/png;base64,${img.thumbnail_base64}`)
                    : this.getImageUrl(img.path)),
                  thumbnailUrl: img.thumbnail_base64 
                    ? (img.thumbnail_base64.startsWith('data:') 
                        ? img.thumbnail_base64 
                        : `data:image/png;base64,${img.thumbnail_base64}`)
                    : this.getImageUrl(img.path),
                  is_joined: false
                };
                console.log(`[HIERARCHY_APPLY]   - ${imageObj.filename}: url=${imageObj.url?.substring(0, 50)}...`);
                return imageObj;
              });
              
              // Update state
              state.levels[levelIndex].images = imagesWithUrls;
              state.levels[levelIndex].selectedImages = [];
              state.levels[levelIndex].selected = imagesWithUrls[0]?.id || null;
              
              console.log(`[HIERARCHY_APPLY] Level ${levelIndex + 1}: APPLIED ${imagesWithUrls.length} images to state.levels[${levelIndex}]`);
              console.log(`[HIERARCHY_APPLY] Level ${levelIndex + 1}: state.levels[${levelIndex}].images.length = ${state.levels[levelIndex].images.length}`);
            } else {
              console.log(`[HIERARCHY_APPLY] Level ${levelIndex + 1}: No existing files to load`);
            }
          } else {
            console.log(`[HIERARCHY_APPLY] Level ${levelIndex + 1}: No images array in level data`);
          }
          
          // Load markers/lines for this level
          if (levelData.lines && Array.isArray(levelData.lines)) {
            state.levels[levelIndex].lines = levelData.lines;
            console.log(`[HIERARCHY_APPLY] Level ${levelIndex + 1}: Loaded ${levelData.lines.length} markers`);
          }
        } else {
          console.log(`[HIERARCHY_APPLY] ERROR: Invalid level index ${levelIndex}, skipping (max: ${state.levels.length - 1})`);
        }
      });
    } else {
      console.log('[HIERARCHY_APPLY] ERROR: Hierarchy is not an array:', typeof hierarchy);
    }
    
    // Emit the updated state to trigger UI refresh
    // Create a new levels array with new level objects to ensure Angular detects changes
    console.log('[HIERARCHY_APPLY] Emitting updated state via _sessionState$.next()...');
    const newLevels = state.levels.map((level, idx) => ({ ...level }));
    this._sessionState$.next({ ...state, levels: newLevels });
    console.log('[HIERARCHY_APPLY] ========== Hierarchy application complete ==========');
  }

  /**
   * Add images to a specific level (for local loading)
   */
  addImagesToLevel(levelIndex: number, images: ImageInfo[]): void {
    const state = this._sessionState$.value;
    if (state && levelIndex < state.levels.length) {
      // Add to existing images
      state.levels[levelIndex].images.push(...images);
      if (!state.levels[levelIndex].selected && images.length > 0) {
        state.levels[levelIndex].selected = images[0].id;
      }
      this._sessionState$.next({ ...state });
      console.log(`[LOCAL] Added ${images.length} images to level ${levelIndex + 1}`);
    }
  }

  /**
   * Clear a specific level
   */
  clearLevel(levelIndex: number): void {
    const state = this._sessionState$.value;
    if (state && levelIndex < state.levels.length) {
      state.levels[levelIndex] = this.createEmptyLevel(levelIndex, `Level-${levelIndex + 1}`);
      this._sessionState$.next({ ...state });
      console.log(`[LOCAL] Cleared level ${levelIndex + 1}`);
    }
  }

  /**
   * Clear all levels from a specific index onwards
   */
  clearLevelsFrom(startLevelIndex: number): void {
    const state = this._sessionState$.value;
    if (state) {
      for (let i = startLevelIndex; i < state.levels.length; i++) {
        state.levels[i] = this.createEmptyLevel(i, `Level-${i + 1}`);
      }
      this._sessionState$.next({ ...state });
      console.log(`[LOCAL] Cleared levels from ${startLevelIndex + 1} onwards`);
    }
  }
}

